import React from 'react';

import { ZStack } from '@rbilabs/universal-components';
import { Platform, StyleSheet, ViewStyle } from 'react-native';

import Picture from 'components/picture';
import { Box, BoxProps, Typography, TypographyProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { layout, primitive, spacing } from 'styles/constants';

import { headerWidgetTheme } from './theme';

/** Pre-migration Header `_web` sizes; native used headerOne (24px / `2xl`). */
const nativeHeroHeadingStyle = {
  pt: '$5',
  pb: '$2',
  fontSize: '2xl',
  lineHeight: 29,
} as const;

const getHeroHeadingStyle = (isDesktop: boolean) => {
  if (Platform.OS !== 'web') {
    return nativeHeroHeadingStyle;
  }
  return isDesktop
    ? ({ pt: '$4', pb: '$3', fontSize: '8xl', lineHeight: '2xs' } as const)
    : ({ pt: '$4', pb: '$3', fontSize: '5xl', lineHeight: '2xs' } as const);
};

export const StyledHeroHeading = (props: TypographyProps) => {
  const isDesktop = useIsDesktopBreakpoint();
  // Platform split is required: applying web tokens via isDesktop alone oversized native.
  const { pt, pb, fontSize, lineHeight } = getHeroHeadingStyle(isDesktop);

  return (
    <Typography
      variant="headerOne"
      pt={pt}
      pb={pb}
      px="$6"
      textAlign="center"
      color={primitive.$mayoEggWhite}
      fontSize={fontSize}
      lineHeight={lineHeight}
      {...props}
    />
  );
};

export const HeadingContainer = ({ children, style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.headingContainer, style]}>
    {children}
  </Box>
);

export const Heading = (props: TypographyProps) => (
  <Typography
    variant="headerOne"
    textAlign="center"
    m="$0"
    p="$0"
    fontSize="5xl"
    lineHeight="sm"
    color={headerWidgetTheme.textColor}
    style={styles.heading}
    {...props}
  />
);

export const Tagline = (props: TypographyProps) => (
  <Typography
    variant="headerTwo"
    textAlign="center"
    m="$0"
    p="$0"
    fontSize="3xl"
    lineHeight="xl"
    color={headerWidgetTheme.textColor}
    style={{ fontWeight: '400', position: 'relative' }}
    {...props}
  />
);

export const Container = ZStack.withConfig({
  height: '440px',
});

export const Top = ({ children, style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.top, style]}>
    {children}
  </Box>
);

export const Bottom = ({ children, style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.bottom, style]}>
    {children}
  </Box>
);

export const ImageContainer = ({ children, style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.imageContainer, style]}>
    {children}
  </Box>
);

export const Image = Picture.withConfig({
  borderRadius: 'md',
  width: '90%',
});

const styles = StyleSheet.create({
  headingContainer: {
    flexDirection: headerWidgetTheme.headerFlexDirection as ViewStyle['flexDirection'],
  },
  heading: {
    letterSpacing: -2,
    fontWeight: '900',
    marginTop: -6,
    marginBottom: -6,
  },
  top: {
    background: headerWidgetTheme.background,
    paddingBottom: spacing.$32,
    paddingTop: spacing.$8,
    width: layout.full,
  } as ViewStyle,
  bottom: {
    marginTop: spacing.$48,
    width: layout.full,
  },
  imageContainer: {
    alignItems: 'center',
  },
});
